import { BaseListStore } from '@monorepo/controlled/src/stores/base-list.store';
import { SettingsStore } from '@monorepo/controlled/src/stores/settings.store';
import { BidderDomainTableModel, CampaignDomainTagCounters, BidderDomainCounter } from '../models/bidder-domains.model';
import { BidderDomainsApi } from '../apis/bidder-domains.api';
import { bidderDomainsFilterPrototypes, filterPrototypesByProp } from '../components/filters-menu/filters';
import { FiltersMap, FilterStore } from '@monorepo/controlled/src/stores/filter.store';
import { TableStore } from '@monorepo/controlled/src/stores/table.store';
import { FilterModel } from '@monorepo/controlled/src/models/filter.model';
import { Statuses } from '../enums/status';
import { StringActions } from '@monorepo/tools/src/lib/utils/string';

export class BidderDomainsStore {
	campaignDomainTagCounters = new BaseListStore<CampaignDomainTagCounters>({
		listFunc: BidderDomainsApi.getCampaignDomainTagCounters as any,
		listPath: 'campaign_domain_tag_counters',
		model: CampaignDomainTagCounters,
	});

	pausedInCache = new BaseListStore<BidderDomainCounter>({
		listFunc: BidderDomainsApi.getNotWorkingDomainCounters as any,
		listPath: 'campaign_not_working_domain_counters',
		model: BidderDomainCounter,
	});

	domainsInTrash = new BaseListStore<BidderDomainCounter>({
		listFunc: BidderDomainsApi.getCampaignTrashDomainCounters as any,
		listPath: 'campaign_trash_domain_counters',
		model: BidderDomainCounter,
	});

	settingsStore: SettingsStore;

	constructor(settingsStore: SettingsStore) {
		this.settingsStore = settingsStore;
		this.createDefaultFilters();
	}

	createDefaultFilters() {
		let defaultFilters: FiltersMap = new Map();

		const prototype = bidderDomainsFilterPrototypes.find(f => f.MenuComponent === 'Status');

		const key = 'bidderdomainsmanager';

		if (prototype) {
			defaultFilters = new Map([
				[
					1,
					new FilterModel({
						index: 1,
						label: `Status is ${Statuses.Active}, ${Statuses.Paused}`,
						action: StringActions.Equals,
						value: [Statuses.Active, Statuses.Paused],
						prototype,
					}),
				],
			]);
		}

		if (!this.settingsStore.getPages().get(key)) {
			this.settingsStore.getPages().set(key, { tableStore: new TableStore() });
		}

		const bidderCampaignFilterByKey = new FilterStore({
			key,
			currentFilters: defaultFilters,
			filterPrototypesByProp,
		});

		this.settingsStore.getPages().get(key)?.tableStore.setFiltersStore(key, bidderCampaignFilterByKey);
	}

	getTableData(): BidderDomainTableModel[] {
		const domains = this.campaignDomainTagCounters.getList();
		const pausedList = this.pausedInCache.getList();
		const trashList = this.domainsInTrash.getList();
		const pausedInCache = new Map(pausedList.map(i => [i.campaign_id, i.counter]));
		const domainsInTrash = new Map(trashList.map(i => [i.campaign_id, i.counter]));
		const pausedStatus = new Map(pausedList.map(i => [i.campaign_id, i.status]));
		const trashStatus = new Map(trashList.map(i => [i.campaign_id, i.status]));
		const byCampaignId = new Map(domains.map(d => [d.campaign_id, d]));

		const allIds = new Set([
			...domains.map(d => d.campaign_id),
			...pausedList.map(i => i.campaign_id),
			...trashList.map(i => i.campaign_id),
		]);

		return [...allIds].map(campaignId => {
			const d = byCampaignId.get(campaignId);
			return {
				campaign_id: campaignId,
				status: d?.status ?? pausedStatus.get(campaignId) ?? trashStatus.get(campaignId) ?? Statuses.New,
				white_list: d?.white_list ?? [],
				black_list: d?.black_list ?? [],
				last_updated: d?.last_updated ?? '',
				black_list_filter: d?.black_list?.map(i => i.tag) ?? [],
				white_list_filter: d?.white_list?.map(i => i.tag) ?? [],
				paused_in_cache: pausedInCache.get(campaignId),
				domains_in_trash: domainsInTrash.get(campaignId),
			};
		});
	}
}
